// ============================================================
// COUNTDOWN - Pure countdown logic for round-based condition
// expiration, death processing, and regeneration.
// Extracted from useCombat computeCountdown.
// ============================================================
import { UNDYING_REASON } from './undyingReason';
import {
  condType,
  condDuration,
  COND_NAMES,
  buildCondCode,
  decodeCond,
  haywireSeverityName,
  isTimedFamily,
} from './conditions';
import { parseExposure, exposureLabel } from './exposures';
import { tickHallucination } from './hallucinationCode';
import type { Target, NarrativeEntry } from '../types';
import { activeProtagonist, resolveDeathFloor } from './survival/protagonist';

/** Conditions to remove when a target dies */
export const REMOVE_ON_DEATH = new Set(['Dy', 'U', 'K', 'F', 'S', 'B', 'D']);

/** Condition types that tick down each round */
// 'Rg' = regeneration halted by Suppressing(X) (v8.286.0);
// 'Dsm' = guard beaten aside by Disarming(X) (v8.287.0).
const COUNTDOWN_TYPES = new Set(['S', 'B', 'D', 'Dy', 'Hw', 'Rg', 'Dsm']);

export interface CountdownResult {
  newConds: string[];
  narrativeEntries: NarrativeEntry[];
  targetKilled: boolean;
  regenHealed: number;
}

/** What one round does to one condition: its new code (null = gone), a note, and whether Dying ran out. */
interface ConditionTick {
  code: string | null;
  note: string;
  died: boolean;
}

/**
 * True when a condition of this type and duration loses a round this tick.
 * The timed families (Pen:, Dn:, Ex:) carry their own round count, so they
 * tick only while one is left.
 */
function ticksDown(ct: string, dur: number): boolean {
  return dur > 0 && (COUNTDOWN_TYPES.has(ct) || isTimedFamily(ct));
}

/** The name a countdown note gives a condition. */
function countdownLabel(c: string, ct: string): string {
  if (ct.startsWith('Pen:')) return (decodeCond(c) || c).replace(/ \(\d+ rounds?\)/, '');
  if (ct.startsWith('Ex:')) return exposureLabel(parseExposure(c)!);
  return COND_NAMES[ct] || ct;
}

/**
 * The note for a condition that lost a round and is still running. Haywire
 * narrates its DECAY in severity names - "the field weakens" is the official
 * per-round behaviour (v8.198.6).
 */
function decayNote(ct: string, label: string, dur: number, newDur: number): string {
  if (ct === 'Hw') return 'Haywire weakens: ' + haywireSeverityName(dur) + ' \u2192 ' + haywireSeverityName(newDur);
  return label + ' ' + dur + '\u2192' + newDur;
}

/** One round of one condition, or null when it does not count down. */
function tickCondition(c: string, targetName: string): ConditionTick | null {
  const ct = condType(c);
  // Hallucinating (v8.357.0) has its own two clocks - a STEP falls a
  // rung, a HOLD counts a round off, `:0` never ticks - and its own
  // lines, so it does not go through the generic decrement below.
  if (ct === 'Hl') return { ...tickHallucination(c, targetName || 'The target'), died: false };
  const dur = condDuration(c);
  if (!ticksDown(ct, dur)) return null;
  const label = countdownLabel(c, ct);
  const newDur = dur - 1;
  if (newDur <= 0) return { code: null, note: label + ' expired', died: ct === 'Dy' };
  return { code: buildCondCode(ct, newDur), note: decayNote(ct, label, dur, newDur), died: false };
}

/**
 * One round of every condition. Notes run from the LAST condition to the
 * first - the order the original backwards splice loop wrote them in, which
 * the log still shows.
 */
function tickConditions(conds: string[], targetName: string) {
  const ticks = conds.map((c) => tickCondition(c, targetName) ?? { code: c, note: '', died: false });
  return {
    newConds: ticks.map((t) => t.code).filter((c): c is string => c !== null),
    notes: ticks
      .map((t) => t.note)
      .filter(Boolean)
      .reverse(),
    dyingExpired: ticks.some((t) => t.died),
  };
}

/**
 * What stops an expired Dying clock from killing: Undying, then the
 * protagonist's death floor. Null means nothing does.
 *
 * Undying says "cannot die by normal means" and has always been honoured
 * at the critical-hit site - but never here, so an Undying combatant whose
 * clock ran out was killed anyway. Same line, same class of bug, fixed with
 * the floor it belongs beside.
 */
function deathFloor(target: Target, protagonistRules: boolean): { conditions: string[]; note: string } | null {
  if (target.undying) return { conditions: [], note: UNDYING_REASON };
  return resolveDeathFloor(activeProtagonist(target, protagonistRules), 'dyingExpired');
}

/** True for a condition the dead keep: not in REMOVE_ON_DEATH, and not a running `Pen:` clock. */
function survivesDeath(c: string): boolean {
  const ct = condType(c);
  if (REMOVE_ON_DEATH.has(ct)) return false;
  return !(ct.startsWith('Pen:') && condDuration(c) > 0);
}

interface DeathOutcome {
  conds: string[];
  killed: boolean;
  notes: string[];
}

/**
 * Settle an expired Dying clock: a floor adds its conditions and its note;
 * with no floor the target dies, shedding what the dead cannot hold and
 * gaining 'K'.
 */
function settleDeath(target: Target, protagonistRules: boolean, conds: string[]): DeathOutcome {
  const floor = deathFloor(target, protagonistRules);
  if (!floor) return { conds: [...conds.filter(survivesDeath), 'K'], killed: true, notes: [] };
  const floored = [...conds];
  for (const c of floor.conditions) if (!floored.includes(c)) floored.push(c);
  return { conds: floored, killed: false, notes: [floor.note] };
}

/** True when regeneration may heal this round. */
function canRegenerate(target: Target, targetKilled: boolean): boolean {
  const regenerates = (target.regeneration || 0) > 0 && !target.horde?.isHorde;
  if (!regenerates || targetKilled) return false;
  // Suppressing(X)'s Rg condition halts it (v8.286.0) - checked against the
  // PRE-TICK list, so the round Rg expires in is still a suppressed round.
  return !(target.conditions || []).some((c) => condType(c) === 'Rg');
}

/** Regeneration at the start of the turn: wounds healed, and its note (none when nothing healed). */
function regenerate(target: Target, targetKilled: boolean): { healed: number; notes: string[] } {
  if (!canRegenerate(target, targetKilled)) return { healed: 0, notes: [] };
  const { current: wCur, max: wMax } = target.wounds;
  if (wCur >= wMax) return { healed: 0, notes: [] };
  const regen = target.regeneration || 0;
  const healed = Math.min(regen, wMax - wCur);
  const note = 'Regen(' + regen + '): +' + healed + ' wounds (' + wCur + '\u2192' + (wCur + healed) + '/' + wMax + ')';
  return { healed, notes: [note] };
}

const DEAD_LINE = '\uD83D\uDC80 TARGET IS DEAD!';

/** An info log entry stamped now. */
function infoEntry(text: string, targetName: string): NarrativeEntry {
  return { type: 'info', text, targetName, timestamp: Date.now() };
}

/** The log: the countdown line (when a death left any notes), then the death line. */
function countdownEntries(notes: string[], targetKilled: boolean, targetName: string): NarrativeEntry[] {
  if (!targetKilled) return [infoEntry('\u23F1 Round countdown: ' + notes.join(', '), targetName)];
  const cdNotes = notes.filter((n) => n !== DEAD_LINE);
  const entries = cdNotes.length > 0 ? [infoEntry('\u23F1 Round countdown: ' + cdNotes.join(', '), targetName)] : [];
  return [...entries, infoEntry(DEAD_LINE, targetName)];
}

/**
 * Pure computation: decrement timed conditions for round countdown.
 *
 * `enabled` is the Round Countdown rule, passed in rather than read off
 * the target: it is a session-wide GM house rule, and `engine/` may not
 * reach for React session state. Callers resolve it once and hand it
 * down. Returns null if nothing to do (rule off, already dead, or no
 * changes).
 */
export function computeCountdownPure(
  target: Target,
  targetName: string,
  enabled: boolean,
  /**
   * The table's protagonist ruling. Absent means off, so every existing
   * caller keeps the behaviour it had.
   */
  protagonistRules = false,
): CountdownResult | null {
  if (!enabled || (target.conditions || []).includes('K')) return null;

  const ticked = tickConditions(target.conditions || [], targetName);
  // THE SECOND PLACE A COMBATANT CAN DIE, and the easiest to miss: this
  // function has four callers, two of which run on a MISSED ATTACK and a
  // WEAPON JAM. Intercepting here rather than in them is what makes the
  // guarantee hold however the tick was reached.
  const death: DeathOutcome = ticked.dyingExpired
    ? settleDeath(target, protagonistRules, ticked.newConds)
    : { conds: ticked.newConds, killed: false, notes: [] };
  const regen = regenerate(target, death.killed);
  const notes = [...ticked.notes, ...death.notes, ...regen.notes];
  if (notes.length === 0 && regen.healed === 0) return null;

  const narrativeEntries = countdownEntries(notes, death.killed, targetName || '');
  return { newConds: death.conds, narrativeEntries, targetKilled: death.killed, regenHealed: regen.healed };
}
